import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { type ReactNode, useEffect, useMemo, useState } from 'react'

import { getHighestRole } from '@ankor-io/common/domain/iam/account'
import { AccountPreferenceKeys, type AccountPreferences, type Me, Role } from '@ankor-io/common/types/account'

import { UserContext } from './UserContext'
import type { UserContextType } from './types'

export function UserContextProvider({ children }: { children: ReactNode }) {
  // get kindeUser details
  const { user: kindeUser, isAuthenticated, getAccessToken } = useKindeAuth()

  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<UserContextType | null>(null)
  const [activeOrganizationId, setActiveOrganizationId] = useState<string | null>(null)
  const [activeConnectedAccountId, setActiveConnectedAccountId] = useState<string | null>(null)

  const activeOrganizationRole: Role = useMemo(() => {
    return activeOrganizationId && user?.organizations[activeOrganizationId]
      ? getHighestRole(user?.organizations[activeOrganizationId].roles) || Role.VIEWER
      : Role.VIEWER
  }, [user, activeOrganizationId])

  useEffect(() => {
    async function fetchApiUser() {
      try {
        setIsLoading(true)
        if (kindeUser) {
          const userData = {
            id: kindeUser.id,
            givenName: kindeUser?.givenName || '',
            familyName: kindeUser?.familyName || '',
            email: kindeUser?.email || '',
            organizations: {},
            preferences: {} as AccountPreferences,
          }

          const res = await fetch('/api/iam/me', {
            headers: {
              Authorization: `Bearer ${await getAccessToken()}`,
            },
          })

          if (res.ok) {
            // Save response from iam/me into userData
            const accountData = (await res.json()) as Me
            userData.organizations = accountData.organizations
            userData.preferences = accountData.preferences || {}

            // If the user has an active organization inside preference, use it, otherwise use the first organization
            const firstOrgId =
              userData.preferences?.[AccountPreferenceKeys.ACTIVE_ORGANIZATION_ID] ||
              Object.keys(accountData.organizations)[0]
            setActiveOrganizationId(firstOrgId)

            // If the user has a active connected account inside preference, use it
            const activeConnectedAccountId =
              userData.preferences?.[AccountPreferenceKeys.ACTIVE_CONNECTED_ACCOUNT_ID] || null
            setActiveConnectedAccountId(activeConnectedAccountId)
          } else {
            console.error('Failed to fetch account data', await res.text())
          }
          setUser(userData)
        }

        setIsLoading(false)
      } catch (error) {
        console.error(error)
      }
    }

    if (!isAuthenticated) {
      setUser(null)
      setActiveOrganizationId(null)
      return
    }

    if (kindeUser) {
      fetchApiUser()
    }
  }, [kindeUser, isAuthenticated, getAccessToken])

  const updateOrganization = (orgId: string, name: string, description: string) => {
    if (!user || !user.organizations[orgId]) {
      return
    }

    const updatedUser = { ...user }
    const updatedOrganization = { ...updatedUser.organizations[orgId], name, description }
    updatedUser.organizations[orgId] = updatedOrganization

    setUser(updatedUser)

    // Save the active organization id to the preferences
    updatePreferences({ activeOrganizationId: orgId })
  }

  const updatePreferences = async (updatedPreferences: AccountPreferences) => {
    const res = await fetch('/api/iam/me/preferences', {
      method: 'PUT',
      body: JSON.stringify({ preferences: updatedPreferences }),
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
    })

    if (res.ok) {
      // The type error occurs because spreading user creates a new object that loses the type information
      // We need to explicitly cast it back to UserContextType to maintain the type safety
      const updatedUser: UserContextType = {
        ...user!,
        preferences: { ...user!.preferences, ...updatedPreferences },
      }

      setUser(updatedUser)
    }
  }

  const updateActiveOrganizationId = (orgId: string) => {
    if (activeOrganizationId === orgId) {
      return
    }

    // When we update the activeOrganizationId, we need to reset the activeConnectedAccountId as well.
    // This ensures that we don't have an activeConnectedAccountId that is not associated with the activeOrganizationId
    setActiveOrganizationId(orgId)
    setActiveConnectedAccountId(null)
    updatePreferences({ activeOrganizationId: orgId, activeConnectedAccountId: '' })
  }

  const updateActiveConnectedAccountId = (connectedAccountId: string) => {
    if (activeConnectedAccountId === connectedAccountId) {
      return
    }

    setActiveConnectedAccountId(connectedAccountId)
    updatePreferences({ activeConnectedAccountId: connectedAccountId })
  }

  return (
    <UserContext.Provider
      value={{
        user,
        isLoading,
        organizations: user?.organizations || {},
        activeOrganizationId,
        activeConnectedAccountId,
        activeOrganizationRole,
        preferences: user?.preferences || {},
        updateOrganization,
        updateActiveOrganizationId,
        updateActiveConnectedAccountId,
        updatePreferences,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
